import React, { useState, useCallback, useMemo, useContext } from "react";
import { intl, LOCALES_FLAG, LanguageBaseContext } from "dmsbasereact";
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { fetchData } from "dmsbasereact";
import { UserRole } from "../../constants/UserRole";

export const DmsHelpCenterUploadManual = () => {
  const { language: currentLanguage } = useContext(LanguageBaseContext);

  // Define at render time to ensure translations are loaded
  // eslint-disable-next-line react-hooks/exhaustive-deps -- currentLanguage is required for i18n updates
  const TARGET_ROLES_OPTIONS = useMemo(() => [
    { value: UserRole.PHARMACIST, label: intl.get("Pharmacist").d("Pharmacist") },
    { value: UserRole.INSTITUTION_RESPONSIBLE, label: intl.get("InstitutionResponsible").d("Institution Responsible") },
    { value: UserRole.EUROFARMA_GESTAO_LENA, label: intl.get("GestaoLena").d("Gestão Lena") },
    { value: UserRole.EUROFARMA_DIRETORIA, label: intl.get("Diretoria").d("Diretoria") },
    { value: UserRole.EUROFARMA_REGULATORIO, label: intl.get("Regulatorio").d("Regulatório") },
  ], [currentLanguage]);

  const [selectedFile, setSelectedFile] = useState(null);
  const [slug, setSlug] = useState("");
  const [language, setLanguage] = useState("");
  const [targetRoles, setTargetRoles] = useState([]);
  const [title, setTitle] = useState("");
  const [loading, setLoading] = useState(false);
  const [responseMessage, setResponseMessage] = useState(null);
  const [responseSeverity, setResponseSeverity] = useState("info");

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0]);
  };

  const handleUpload = useCallback(async () => {
    if (!selectedFile || !slug || !language) {
      setResponseMessage(
        intl
          .get("DmsHelpCenterUploadManual.fieldsRequired")
          .d("Please select a file and fill in the slug and language.")
      );
      setResponseSeverity("error");
      return;
    }

    setLoading(true);
    setResponseMessage(null);

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("slug", slug);
    formData.append("language", language);
    formData.append("target_roles", targetRoles.join(","));
    if (title) {
      formData.append("title", title);
    }



    try {
      const response = await fetchData(
        "/help-center/manual_upload",
        "POST",
        true, // requires authentication
        formData,
        false, // handle_failures
        null, // handle_swal
        null, // ipv4
        null, // port
        "multipart/form-data" // content_type
      );

      if (response.status >= 200 && response.status < 300) {
        setResponseMessage(
          intl
            .get("DmsHelpCenterUploadManual.uploadSuccess")
            .d("Manual uploaded successfully!")
        );
        setResponseSeverity("success");
        setSelectedFile(null);
        setSlug("");
        setLanguage("");
        setTargetRoles([]);
        setTitle("");
      } else {
        const errorDetail = response.data?.detail || response.data || response.statusText;
        setResponseMessage(
          intl
            .get("DmsHelpCenterUploadManual.uploadError", { errorDetail })
            .d("Error uploading manual: {errorDetail}")
        );
        setResponseSeverity("error");
      }
    } catch (error) {
      setResponseMessage(
        intl
          .get("DmsHelpCenterUploadManual.uploadFailed", { errorMessage: error.message })
          .d("Request failed: {errorMessage}")
      );
      setResponseSeverity("error");
    } finally {
      setLoading(false);
    }
  }, [selectedFile, slug, language, targetRoles, title]);

  return (
    <Box sx={{ p: 3, maxWidth: 600, mx: "auto" }}>
      <Typography variant="h5" component="h1" gutterBottom>
        {intl.get("DmsHelpCenterUploadManual.title").d("Help Center Manual Upload")}
      </Typography>

      <Stack spacing={3} mt={3}>
        <TextField
          label={intl.get("DmsHelpCenterUploadManual.slugLabel").d("Manual Slug")}
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          fullWidth
          required
          helperText={intl.get("DmsHelpCenterUploadManual.slugHelper").d("E.g.: patient-management, dispensation, home")}
        />

        <FormControl fullWidth required>
          <InputLabel>{intl.get("DmsHelpCenterUploadManual.languageLabel").d("Language")}</InputLabel>
          <Select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            label={intl.get("DmsHelpCenterUploadManual.languageLabel").d("Language")}
          >
            {Object.entries(LOCALES_FLAG).map(([label, value]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label={intl.get("DmsHelpCenterUploadManual.titleLabel").d("Manual Title")}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          helperText={intl.get("DmsHelpCenterUploadManual.titleHelper").d("Optional - if not provided, uses the file name")}
        />

        <FormControl fullWidth>
          <InputLabel>{intl.get("DmsHelpCenterUploadManual.targetRolesLabel").d("User Categories")}</InputLabel>
          <Select
            multiple
            value={targetRoles}
            onChange={(e) => setTargetRoles(e.target.value)}
            input={<OutlinedInput label={intl.get("DmsHelpCenterUploadManual.targetRolesLabel").d("User Categories")} />}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((value) => {
                  const option = TARGET_ROLES_OPTIONS.find((o) => o.value === value);
                  return <Chip key={value} label={option?.label || value} size="small" />;
                })}
              </Box>
            )}
          >
            {TARGET_ROLES_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
            {intl.get("DmsHelpCenterUploadManual.targetRolesHelper").d("Leave empty to show to all users")}
          </Typography>
        </FormControl>

        <input
          accept=".pdf"
          style={{ display: "none" }}
          id="manual-upload-button"
          type="file"
          onChange={handleFileChange}
        />
        <label htmlFor="manual-upload-button">
          <Button
            variant="outlined"
            component="span"
            startIcon={<CloudUploadIcon />}
            fullWidth
          >
            {selectedFile
              ? selectedFile.name
              : intl.get("DmsHelpCenterUploadManual.selectFile").d("Select PDF File")}
          </Button>
        </label>

        <Button
          variant="contained"
          color="primary"
          onClick={handleUpload}
          disabled={!selectedFile || !slug || !language || loading}
          startIcon={loading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
          fullWidth
        >
          {loading
            ? intl.get("DmsHelpCenterUploadManual.uploading").d("Uploading...")
            : intl.get("DmsHelpCenterUploadManual.uploadButton").d("Upload Manual")}
        </Button>

        {responseMessage && (
          <Alert severity={responseSeverity}>{responseMessage}</Alert>
        )}
      </Stack>
    </Box>
  );
};
